import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormCard } from "../FormCard";

const UNASSIGNED_TARIFF_KEY = "__unassigned_tariff__";
const UNASSIGNED_SITE_KEY = "__unassigned_site__";
const UNASSIGNED_CONNECTOR_KEY = "__unassigned_connector__";

function groupTariffs(tariffs, t) {
  const tariffMap = new Map();

  const getTariffGroup = (tariffKey, tariffName) => {
    if (!tariffMap.has(tariffKey)) {
      tariffMap.set(tariffKey, {
        tariffKey,
        tariffName,
        sites: new Map(),
      });
    }

    return tariffMap.get(tariffKey);
  };

  const getSiteGroup = (tariffGroup, siteKey, siteName) => {
    if (!tariffGroup.sites.has(siteKey)) {
      tariffGroup.sites.set(siteKey, {
        siteKey,
        siteName,
        connectors: [],
      });
    }

    return tariffGroup.sites.get(siteKey);
  };

  tariffs.forEach((tariff) => {
    const tariffKey = tariff.tariffPublicId || tariff.tariffName || UNASSIGNED_TARIFF_KEY;
    const tariffName = tariff.tariffName || "-";
    const tariffGroup = getTariffGroup(tariffKey, tariffName);

    const connectors = tariff.chargingConnectors ?? [];

    if (connectors.length === 0) {
      const siteGroup = getSiteGroup(
        tariffGroup,
        UNASSIGNED_SITE_KEY,
        t("customer.tariffsUnassigned")
      );
      siteGroup.connectors.push({
        connectorKey: UNASSIGNED_CONNECTOR_KEY,
        connectorName: t("customer.tariffsNoConnectors"),
      });
      return;
    }

    connectors.forEach((connector) => {
      const siteKey =
        connector.chargingSitePublicId ||
        connector.chargingSiteName ||
        UNASSIGNED_SITE_KEY;
      const siteName = connector.chargingSiteName || t("customer.tariffsUnassigned");
      const connectorKey =
        connector.chargingConnectorPublicId ||
        connector.chargingConnectorName ||
        UNASSIGNED_CONNECTOR_KEY;
      const connectorName = connector.chargingConnectorName || t("booking.unnamedConnector");

      const siteGroup = getSiteGroup(tariffGroup, siteKey, siteName);
      siteGroup.connectors.push({
        connectorKey,
        connectorName,
      });
    });
  });

  return [...tariffMap.values()]
    .map((tariffGroup) => {
      const sites = [...tariffGroup.sites.values()]
        .map((siteGroup) => ({
          ...siteGroup,
          connectors: [...siteGroup.connectors].sort((a, b) =>
            a.connectorName.localeCompare(b.connectorName)
          ),
        }))
        .sort((a, b) => a.siteName.localeCompare(b.siteName));

      return {
        tariffKey: tariffGroup.tariffKey,
        tariffName: tariffGroup.tariffName,
        sites,
        rowSpan: sites.reduce(
          (total, siteGroup) => total + Math.max(siteGroup.connectors.length, 1),
          0
        ),
      };
    })
    .sort((a, b) => a.tariffName.localeCompare(b.tariffName));
}

const CustomerTariffsTable = ({ tariffs = [], canOpenTariff = false }) => {
  const { t } = useTranslation();
  const groupedTariffs = useMemo(() => groupTariffs(tariffs, t), [tariffs, t]);

  return (
    <FormCard
      title={t("customer.tariffsTitle")}
      description={t("customer.tariffsDescription")}
    >
      <div className="overflow-x-auto">
        <table className="table-default tariffs-no-hover w-full text-sm">
          <thead>
            <tr>
              <th className="text-sm">{t("entity.tariff")}</th>
              <th className="text-sm">{t("entity.site")}</th>
              <th className="text-sm">{t("entity.connector")}</th>
            </tr>
          </thead>
          <tbody>
            {groupedTariffs.length === 0 && (
              <tr>
                <td className="py-3 text-sm" colSpan="3">
                  {t("customer.tariffsEmpty")}
                </td>
              </tr>
            )}

            {groupedTariffs.map((tariffGroup) =>
              tariffGroup.sites.map((siteGroup, siteIndex) => {
                const connectorRows =
                  siteGroup.connectors.length > 0
                    ? siteGroup.connectors
                    : [{ connectorKey: `${siteGroup.siteKey}-empty`, connectorName: "-" }];

                return connectorRows.map((connector, connectorIndex) => {
                  const showTariffCell = siteIndex === 0 && connectorIndex === 0;
                  const showSiteCell = connectorIndex === 0;

                  return (
                    <tr
                      key={[
                        tariffGroup.tariffKey,
                        siteGroup.siteKey,
                        connector.connectorKey,
                        siteIndex,
                        connectorIndex,
                      ].join("-")}
                    >
                      {showTariffCell && (
                        <td rowSpan={tariffGroup.rowSpan} className="align-middle text-sm">
                          {canOpenTariff ? (
                            <Link
                              className="font-medium text-sm"
                              to={`/tariffs/${tariffGroup.tariffKey}?tab=elements`}
                            >
                              {tariffGroup.tariffName}
                            </Link>
                          ) : (
                            <span className="font-medium text-sm">{tariffGroup.tariffName}</span>
                          )}
                        </td>
                      )}

                      {showSiteCell && (
                        <td rowSpan={connectorRows.length} className="align-middle text-sm">
                          <div className="text-sm">{siteGroup.siteName}</div>
                        </td>
                      )}

                      <td className="text-sm">{connector.connectorName}</td>
                    </tr>
                  );
                });
              })
            )}
          </tbody>
        </table>
      </div>
    </FormCard>
  );
};

export default CustomerTariffsTable;
